import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, apiUrl, fmt, getUser } from '../api';
import { Modal, Field, Input, useToast, Loading, Eyebrow, KelvinDot } from '../ui';
import { Icons } from '../icons';

/** Rasmni brauzerda kichraytirib JPEG dataUrl qiladi (max 360px). */
function resizeImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const max = 360;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => reject(new Error('Rasmni o\'qib bo\'lmadi'));
    img.src = URL.createObjectURL(file);
  });
}

export default function Products() {
  const [params] = useSearchParams();
  const [data, setData] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [onlyNoGroup, setOnlyNoGroup] = useState(params.get('onlyNoGroup') === 'true');
  const [brandId, setBrandId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [edit, setEdit] = useState<any>(null);
  const [groups, setGroups] = useState<any[]>([]);
  const [cats, setCats] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [series, setSeries] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [serOpen, setSerOpen] = useState(false);
  const [serName, setSerName] = useState('');
  const [imgVer, setImgVer] = useState(0); // rasm yangilanганда keshni chetlab o'tish
  const fileRef = useRef<HTMLInputElement>(null);
  const [onec, setOnec] = useState<any>(null);
  const [onecBusy, setOnecBusy] = useState(false);
  const serImgRef = useRef<HTMLInputElement>(null);
  const serTargetRef = useRef<number | null>(null);
  const toast = useToast();
  const user = getUser();
  // rastamojka mahsulotni tahrirlaydi (TNVED/guruh), lekin yarata/import qila olmaydi
  const canEdit = ['admin', 'zakaz_operator', 'rastamojka'].includes(user?.role);
  const canCreate = ['admin', 'zakaz_operator'].includes(user?.role);

  const load = () => {
    const qs = new URLSearchParams({ page: String(page), pageSize: '50' });
    if (search) qs.set('search', search);
    if (onlyNoGroup) qs.set('onlyNoGroup', 'true');
    if (brandId) qs.set('brandId', brandId);
    if (categoryId) qs.set('categoryId', categoryId);
    api.get('/products?' + qs.toString()).then(setData).catch((e) => { toast(e.message, true); setData({ items: [], total: 0, pageSize: 50 }); });
  };
  useEffect(() => { load(); }, [page, onlyNoGroup, brandId, categoryId]);
  useEffect(() => {
    api.get('/customs-groups').then(setGroups).catch(() => setGroups([]));
    api.get('/categories').then((tree) => {
      // Daraxtni chuqurlik (depth) bilan yassilash — indentli ko'rsatish uchun
      const flat: any[] = [];
      const walk = (nodes: any[], depth: number) => nodes.forEach((n) => { flat.push({ id: n.id, name: n.name, depth }); if (n.children?.length) walk(n.children, depth + 1); });
      walk(tree, 0);
      setCats(flat);
    }).catch(() => setCats([]));
    api.get('/brands').then(setBrands).catch(() => setBrands([]));
    api.get('/suppliers').then(setSuppliers).catch(() => setSuppliers([]));
    api.get('/series').then(setSeries).catch(() => {});
  }, []);

  const reloadSeries = () => api.get('/series').then(setSeries).catch(() => {});
  const addSeries = async () => {
    if (!serName.trim()) return;
    try { await api.post('/series', { name: serName.trim() }); setSerName(''); toast('Seriya qo\'shildi'); reloadSeries(); }
    catch (e: any) { toast(e.message, true); }
  };
  const uploadSeriesImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; const sid = serTargetRef.current;
    if (!file || !sid) return;
    try {
      const dataUrl = await resizeImage(file);
      await api.post(`/series/${sid}/image`, { dataUrl });
      toast('Rasm saqlandi'); setImgVer((v) => v + 1); reloadSeries(); load();
    } catch (e: any) { toast(e.message, true); }
    if (serImgRef.current) serImgRef.current.value = '';
  };

  // page allaqachon 1 bo'lsa effekt otmaydi — o'zimiz yuklaymiz (ikkilangan so'rov ham bo'lmaydi)
  const onSearch = (e: React.FormEvent) => { e.preventDefault(); if (page !== 1) setPage(1); else load(); };

  const save = async () => {
    try {
      if (edit.id) await api.patch('/products/' + edit.id, edit);
      else await api.post('/products', edit);
      toast('Saqlandi'); setEdit(null); load();
    } catch (e: any) { toast(e.message, true); }
  };
  const remove = async (id: number) => {
    if (!confirm("Mahsulotni o'chirasizmi?")) return;
    try { await api.del('/products/' + id); toast("O'chirildi"); load(); }
    catch (e: any) { toast(e.message, true); }
  };
  const doImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const r = await api.upload('/products/import', file);
      toast(`Import: ${r.created} yangi, ${r.updated} yangilandi, ${r.skipped} o'tkazildi`);
      load();
    } catch (e: any) { toast(e.message, true); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Katalog (Nomenklatura)</h1>
          <p className="page-sub">{data ? fmt(data.total) : '—'} ta mahsulot</p>
        </div>
        <div className="head-actions">
          <button className="btn" onClick={() => setSerOpen(true)}>🖼 Seriyalar</button>
          <button className="btn" onClick={() => api.download('/products/export', 'katalog.xlsx')}><Icons.download size={16} /> Excel</button>
          {canCreate && <button className="btn" onClick={() => setOnec({})}><Icons.download size={16} /> 1C dan yangilash</button>}
          {canCreate && <>
            <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={doImport} />
            <button className="btn" onClick={() => fileRef.current?.click()}><Icons.upload size={16} /> Excel import</button>
            <button className="btn btn-primary" onClick={() => setEdit({ unit: 'pcs' })}><Icons.plus size={16} /> Yangi mahsulot</button>
          </>}
        </div>
      </div>

      <form className="filters" onSubmit={onSearch}>
        <input className="search" placeholder="Artikul / nom bo'yicha qidirish…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input" style={{ width: 160 }} value={brandId} onChange={(e) => { setBrandId(e.target.value); setPage(1); }}>
          <option value="">Barcha brendlar</option>
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select className="input" style={{ width: 240 }} value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setPage(1); }}>
          <option value="">Barcha kategoriyalar</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{'  '.repeat(c.depth) + (c.depth > 0 ? '└ ' : '') + c.name}</option>)}
        </select>
        <label className="chip"><input type="checkbox" checked={onlyNoGroup} onChange={(e) => { setOnlyNoGroup(e.target.checked); setPage(1); }} /> Faqat guruhsizlar</label>
        <button className="btn">Qidirish</button>
      </form>

      {!data ? <Loading /> : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th></th><th>Artikul</th><th>Nomi</th><th>Quvvat</th><th>Brend</th><th>Kategoriya</th>
                  <th>Rastamojka guruhi</th><th className="num">Og'irlik (g)</th><th className="num">Hajm (cm³)</th><th className="num">Korobkada</th><th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p: any) => (
                  <tr key={p.id}>
                    <td style={{ width: 44, padding: '6px 8px 6px 14px' }}>
                      {p.series?.imageMime
                        ? <img src={apiUrl(`/series/${p.series.id}/image`) + '?v=' + imgVer} alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                        : <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--surface-3)' }} />}
                    </td>
                    <td><Link to={'/products/' + p.id} className="code" style={{ color: 'var(--accent)' }}>{p.article}</Link></td>
                    <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><KelvinDot text={p.name} />{p.name}</span></td>
                    <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><KelvinDot text={p.powerLabel} />{p.powerLabel || '—'}</span></td>
                    <td>{p.brand?.name || '—'}</td>
                    <td>{p.category?.name || '—'}</td>
                    <td>{p.customsGroup ? p.customsGroup.name : <span className="badge b-cancelled">yo'q</span>}</td>
                    <td className="num">{p.unitNetKg ? fmt(Number(p.unitNetKg) * 1000, 1) : '—'}</td>
                    <td className="num">{p.unitM3 ? fmt(Number(p.unitM3) * 1e6, 0) : '—'}</td>
                    <td className="num">{fmt(p.boxQty)}</td>
                    <td>{canEdit && <div className="act-row">
                      <button className="act act-edit" title="Tahrirlash" onClick={() => setEdit({ ...p })}><Icons.edit size={16} /></button>
                      {user?.role === 'admin' && <button className="act act-del" title="O'chirish" onClick={() => remove(p.id)}><Icons.trash size={16} /></button>}
                    </div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="filters" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
            <span className="muted">{page} / {totalPages}</span>
            <button className="btn btn-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>›</button>
          </div>
        </>
      )}

      {serOpen && (
        <Modal title="Seriyalar va rasmlar" onClose={() => setSerOpen(false)} large>
          <p className="muted" style={{ marginTop: -6 }}>
            Bitta seriya rasmi barcha quvvat variantlariga tegishli (masalan «Panel Akril ichki D» — 10W/18W/24W uchun bitta rasm).
            Mahsulotga seriya tahrirlash oynasida biriktiriladi.
          </p>
          <input ref={serImgRef} type="file" accept="image/*" hidden onChange={uploadSeriesImage} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <Input placeholder="Yangi seriya nomi (masalan: Panel Akril ichki D)" value={serName} onChange={(e) => setSerName(e.target.value)} />
            <button className="btn btn-primary" onClick={addSeries}><Icons.plus size={15} /> Qo'shish</button>
          </div>
          {series.length === 0 ? <p className="empty">Seriyalar yo'q</p> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10 }}>
              {series.map((s) => (
                <div key={s.id} className="row-card" style={{ margin: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    {s.imageMime
                      ? <img src={apiUrl(`/series/${s.id}/image`) + '?v=' + imgVer} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 9 }} />
                      : <div style={{ width: 44, height: 44, borderRadius: 9, background: 'var(--surface-3)', display: 'grid', placeItems: 'center', color: 'var(--muted-2)', fontSize: 11 }}>rasm</div>}
                    <div style={{ minWidth: 0 }}>
                      <div className="row-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                      <div className="row-sub">{s._count?.products ?? 0} mahsulot</div>
                    </div>
                  </div>
                  <button className="btn btn-sm" onClick={() => { serTargetRef.current = s.id; serImgRef.current?.click(); }}>
                    {s.imageMime ? '↻' : '⬆'} Rasm
                  </button>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {edit && (
        <Modal title={edit.id ? 'Mahsulotni tahrirlash' : 'Yangi mahsulot'} onClose={() => setEdit(null)} large>
          <div className="grid-2">
            <Field label="Artikul *"><Input value={edit.article || ''} onChange={(e) => setEdit({ ...edit, article: e.target.value })} /></Field>
            <Field label="Nomi *"><Input value={edit.name || ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Quvvat"><Input value={edit.powerLabel || ''} onChange={(e) => setEdit({ ...edit, powerLabel: e.target.value })} /></Field>
            <Field label="Muqobil artikul"><Input value={edit.altArticle || ''} onChange={(e) => setEdit({ ...edit, altArticle: e.target.value })} /></Field>
            <Field label="Brend">
              <select className="input" value={edit.brandId || ''} onChange={(e) => setEdit({ ...edit, brandId: e.target.value ? +e.target.value : null })}>
                <option value="">—</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Kategoriya">
              <select className="input" value={edit.categoryId || ''} onChange={(e) => setEdit({ ...edit, categoryId: e.target.value ? +e.target.value : null })}>
                <option value="">—</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{'  '.repeat(c.depth) + (c.depth > 0 ? '└ ' : '') + c.name}</option>)}
              </select>
            </Field>
            <Field label="Rastamojka guruhi">
              <select className="input" value={edit.customsGroupId || ''} onChange={(e) => setEdit({ ...edit, customsGroupId: e.target.value ? +e.target.value : null })}>
                <option value="">—</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </Field>
            <Field label="Seriya (rasm)">
              <select className="input" value={edit.seriesId || ''} onChange={(e) => setEdit({ ...edit, seriesId: e.target.value ? +e.target.value : null })}>
                <option value="">—</option>
                {series.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Birlik">
              <select className="input" value={edit.unit || 'pcs'} onChange={(e) => setEdit({ ...edit, unit: e.target.value })}>
                <option value="pcs">dona</option><option value="m">metr</option><option value="set">komplekt</option>
              </select>
            </Field>
            <Field label="1 dona og'irligi (g)"><Input type="number" step="0.1" value={edit.unitNetKg != null && edit.unitNetKg !== '' ? Math.round(Number(edit.unitNetKg) * 1000 * 100) / 100 : ''} onChange={(e) => setEdit({ ...edit, unitNetKg: e.target.value === '' ? null : Number(e.target.value) / 1000 })} /></Field>
            <Field label="1 dona hajmi (cm³)"><Input type="number" step="0.1" value={edit.unitM3 != null && edit.unitM3 !== '' ? Math.round(Number(edit.unitM3) * 1e6 * 100) / 100 : ''} onChange={(e) => setEdit({ ...edit, unitM3: e.target.value === '' ? null : Number(e.target.value) / 1e6 })} /></Field>
            <Field label="Korobkadagi soni (ixtiyoriy)"><Input type="number" value={edit.boxQty ?? ''} onChange={(e) => setEdit({ ...edit, boxQty: e.target.value })} /></Field>
            <Field label="Korobka brutto (kg, ixtiyoriy)"><Input type="number" step="0.001" value={edit.boxGrossKg ?? ''} onChange={(e) => setEdit({ ...edit, boxGrossKg: e.target.value })} /></Field>
            <Field label="Korobka kubasi (m³)"><Input type="number" step="0.000001" value={edit.boxM3 ?? ''} onChange={(e) => setEdit({ ...edit, boxM3: e.target.value })} /></Field>
            <Field label="1 mesta kg (faktik)"><Input type="number" step="0.001" value={edit.mestaKgFact ?? ''} onChange={(e) => setEdit({ ...edit, mestaKgFact: e.target.value })} /></Field>
            <Field label="1 mesta m³ (faktik)"><Input type="number" step="0.000001" value={edit.mestaM3Fact ?? ''} onChange={(e) => setEdit({ ...edit, mestaM3Fact: e.target.value })} /></Field>
            <Field label="Zavod (yetkazib beruvchi)">
              <select className="input" value={edit.supplierId || ''} onChange={(e) => setEdit({ ...edit, supplierId: e.target.value ? +e.target.value : null })}>
                <option value="">—</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Zavod ichki nomi (CN)"><Input value={edit.supplierNameCn || ''} onChange={(e) => setEdit({ ...edit, supplierNameCn: e.target.value })} /></Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <button className="btn" onClick={() => setEdit(null)}>Bekor</button>
            <button className="btn btn-primary" onClick={save}>Saqlash</button>
          </div>
        </Modal>
      )}

      {onec && (
        <Modal title="1C dan katalogni yangilash" onClose={() => setOnec(null)} large>
          <p className="muted" style={{ marginTop: 0 }}>
            1C nomenklaturasidan nomi, kategoriyasi, <b>og'irligi</b>, <b>hajmi</b> va
            korobkadagi soni olinadi. 1C yagona manba: qiymat bor bo'lsa u yutadi.
            Lekin <b>1C da bo'sh bo'lsa bizdagi o'chirilmaydi</b> — u yerda og'irlik va
            hajm faqat 62% to'ldirilgan.
          </p>
          <p className="muted">
            <b>Kabel</b> va <b>tashqi ta'minot</b> olinmaydi: bu tizim Xitoy importi uchun,
            ular esa mahalliy. Yangi tovarga rastamojka guruhi mavjud tovarlardan
            o'rganilgan qoida bilan qo'yiladi (aniqligi 97%); ishonch past bo'lsa
            <b> guruhsiz</b> qoldiriladi va bosh sahifada ko'rinadi.
          </p>

          {onec.result && (
            <div className="card" style={{ padding: 12, marginBottom: 10 }}>
              <Eyebrow title="Natija" desc={`1C da ${onec.result.feedGoods} tovar, olindi ${onec.result.considered}, tashlandi ${onec.result.skippedCategories}`} />
              <table style={{ marginTop: 8 }}>
                <tbody>
                  <tr><td>Yangi qo'shildi</td><td className="num"><b>{onec.result.created}</b></td></tr>
                  <tr><td>Yangilandi</td><td className="num">{onec.result.updated}</td></tr>
                  <tr><td>Guruh belgilandi</td><td className="num">{onec.result.groupAssigned}</td></tr>
                  <tr><td>Guruhsiz qoldi</td><td className="num">{onec.result.needsReview?.length ?? 0}</td></tr>
                </tbody>
              </table>
              {!!onec.result.needsReview?.length && (
                <div style={{ marginTop: 8, maxHeight: 150, overflowY: 'auto' }}>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Guruh qo'lda belgilanishi kerak:</div>
                  {onec.result.needsReview.map((r: any, i: number) => (
                    <div key={i} className="muted" style={{ fontSize: 12 }}>
                      <span className="mono">{r.article}</span> — {r.name}
                      {r.suggestion ? <span style={{ opacity: 0.7 }}> (taklif: {r.suggestion})</span> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button className="btn" onClick={() => setOnec(null)}>Yopish</button>
            <button className="btn" disabled={onecBusy} onClick={async () => {
              setOnecBusy(true);
              try {
                const r = await api.post('/onec/catalog/sync?dryRun=true');
                setOnec({ ...onec, result: r, preview: true });
              } catch (e: any) { toast(e.message, true); }
              finally { setOnecBusy(false); }
            }}>Avval ko'rsat</button>
            <button className="btn btn-primary" disabled={onecBusy} onClick={async () => {
              setOnecBusy(true);
              try {
                const r = await api.post('/onec/catalog/sync');
                setOnec({ ...onec, result: r, preview: false });
                toast(`${r.created} yangi, ${r.updated} yangilandi`);
                load();
              } catch (e: any) { toast(e.message, true); }
              finally { setOnecBusy(false); }
            }}>{onecBusy ? 'Bajarilmoqda…' : 'Yangilash'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
